import React from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { NotificationActionType, NotificationContext } from '../Core/Notification/state';
import Button from '../Core/Button';
import Modal, { ModalActionBar } from '../Core/Modal';
import Typography from '../Core/Typography';

type Props = {
    logout: () => void;
    isLoggedIn: boolean;
    interval: number; // in minutes
};

const LogoutIdleTimer: React.FC<Props> = ({ logout, interval, isLoggedIn }) => {
    const [isLogoutInfoOpen, setIsLogoutInfoOpen] = React.useState(false);
    const [counter, setCounter] = React.useState(30);
    const sessionTimeOutRef: { current: NodeJS.Timeout | null } = React.useRef(null);
    const editorFocusedRef = React.useRef(false);
    const notificationDispatch = React.useContext(NotificationContext).notificationDispatch;
    const onIdle = () => {
        HandleIdle();
    };
    const { reset } = useIdleTimer({
        onIdle,
        timeout: interval * 60 * 1000 - 30000,
        crossTab: true,
    });

    React.useEffect(() => {
        if (isLogoutInfoOpen) {
            //decrease counter if modal is open
            const timer = setInterval(() => setCounter(counter > 0 ? counter - 1 : counter), 1000);

            return () => clearInterval(timer);
        }
    }, [counter, isLogoutInfoOpen]);

    const HandleIdle = () => {
        if (editorFocusedRef.current) return;

        setIsLogoutInfoOpen(true);
        sessionTimeOutRef.current = setTimeout(handleLogout, 30 * 1000);
    };

    const stayActive = () => {
        setIsLogoutInfoOpen(false);
        clearTimeout(sessionTimeOutRef.current as NodeJS.Timeout);
        setCounter(30);
        reset();
    };

    const handleLogout = () => {
        setIsLogoutInfoOpen(false);
        if (sessionTimeOutRef.current) {
            clearTimeout(sessionTimeOutRef.current as NodeJS.Timeout);
            sessionTimeOutRef.current = null;
        }
        setCounter(30);
        logout();
        reset();
        notificationDispatch({
            type: NotificationActionType.OPEN,
            payload: {
                text: 'You have been logged out due to inactivity.',
                status: 'info',
                autoClose: false,
            },
        });
    };
    React.useEffect(() => {
        const handleEditorActivity = (e: Event) => {
            const customEvent = e as CustomEvent<{ active: boolean }>;
            editorFocusedRef.current = customEvent.detail.active;

            if (customEvent.detail.active) {
                reset();
            }
        };

        window.addEventListener('editor-activity', handleEditorActivity);
        return () => {
            window.removeEventListener('editor-activity', handleEditorActivity);
        };
    }, [reset]);

    return isLoggedIn ? (
        <>
            <Modal isOpen={isLogoutInfoOpen} close={stayActive} title="Session Expiry" size="small">
                <Typography variant="body">
                    Your session will expire in {counter} seconds due to inactivity. Would you like to remain logged in?
                </Typography>
                <ModalActionBar>
                    <Button onClick={stayActive} color="secondary">
                        Confirm
                    </Button>
                </ModalActionBar>
            </Modal>
        </>
    ) : (
        <></>
    );
};

export default LogoutIdleTimer;
